import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, ShieldAlert, Search, Loader2, MapPin, Building2, ExternalLink, Camera, X, Clock, ArrowRightLeft, Sparkles, UserCheck, Archive, BadgeCheck, Globe } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { API } from '../App';
import Header from '../components/Header';
import TagoraSeal from '../components/TagoraSeal';

const Verify = () => {
  const { code: codeParam } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(codeParam || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const scannerRef = useRef(null);

  useEffect(() => {
    if (codeParam) {
      checkCode(codeParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeParam]);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        try { scannerRef.current.stop().catch(() => {}); } catch (_) {}
      }
    };
  }, []);

  // Extract a Tagora ID from any scanned text (raw code, full URL, /ping/<id>, /verify/<id>)
  const extractTagoraId = (raw) => {
    if (!raw) return null;
    const txt = raw.trim();
    // Try to find TGR-XXXX-XXXX-XXXX-XXXX in the string
    const match = txt.match(/TGR-[0-9A-Z]{4}-[0-9A-Z]{4}-[0-9A-Z]{4}-[0-9A-Z]{4}/i);
    if (match) return match[0].toUpperCase();
    // Fallback: try to extract the last path segment from URL
    try {
      const u = new URL(txt);
      const seg = u.pathname.split('/').filter(Boolean).pop();
      if (seg) return seg.toUpperCase();
    } catch (_) {}
    // Otherwise return the raw value if it looks like an ID (starts with TGR or ping_)
    if (/^(TGR-|ping_)/i.test(txt)) return txt;
    return null;
  };

  const startScan = async () => {
    setScanError('');
    setScanning(true);
    // Wait one tick so the DOM container exists
    setTimeout(async () => {
      try {
        const html5Qr = new Html5Qrcode('qr-reader');
        scannerRef.current = html5Qr;
        await html5Qr.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => {
            const id = extractTagoraId(decodedText);
            if (id) {
              try { html5Qr.stop().then(() => html5Qr.clear()).catch(() => {}); } catch (_) {}
              scannerRef.current = null;
              setScanning(false);
              navigate(`/verify/${encodeURIComponent(id)}`);
            }
          },
          () => { /* ignore decode errors per frame */ }
        );
      } catch (err) {
        setScanning(false);
        setScanError(err?.message || 'Camera indisponible. Verifiez les autorisations.');
      }
    }, 100);
  };

  const stopScan = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch (_) {}
    scannerRef.current = null;
    setScanning(false);
  };

  const checkCode = async (c) => {
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.get(`${API}/verify/${encodeURIComponent(c)}`);
      setResult(res.data);
    } catch {
      setResult({ exists: false, valid_format: false, message: 'Erreur reseau' });
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const cleaned = code.trim();
    if (!cleaned) return;
    navigate(`/verify/${encodeURIComponent(cleaned)}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 pb-16">
        <div className="text-center mb-10">
          <ShieldCheck className="w-12 h-12 text-charcoal mx-auto mb-3" strokeWidth={2.5} />
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter font-heading mb-2">Verifier un Tagora ID</h1>
          <p className="text-charcoal/70">Saisissez un identifiant pour verifier son authenticite et son statut.</p>
        </div>

        <form onSubmit={submit} className="card-brutal p-5 mb-4" data-testid="verify-form">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="TGR-XXXX-XXXX-XXXX-XXXX"
              className="input-brutal flex-1 font-mono"
              data-testid="verify-input"
            />
            <button type="submit" disabled={!code.trim() || loading} className="btn-primary flex items-center justify-center gap-2 disabled:opacity-50 sm:shrink-0" data-testid="verify-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Verifier
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <div className="flex-1 h-px bg-charcoal/10" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-charcoal/40">ou</span>
            <div className="flex-1 h-px bg-charcoal/10" />
          </div>
          <button
            type="button"
            onClick={startScan}
            className="mt-3 w-full btn-outline flex items-center justify-center gap-2"
            data-testid="verify-scan-btn"
          >
            <Camera className="w-4 h-4" /> Scanner avec la camera
          </button>
          {scanError && (
            <p className="mt-2 text-xs text-danger text-center">{scanError}</p>
          )}
        </form>

        {/* Camera scan modal */}
        {scanning && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="verify-scan-modal">
            <div className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl w-full max-w-md overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b-2 border-charcoal">
                <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                  <Camera className="w-5 h-5" /> Scanner un code
                </h3>
                <button onClick={stopScan} className="text-charcoal/60 hover:text-charcoal" data-testid="verify-scan-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4">
                <div id="qr-reader" className="w-full rounded-xl overflow-hidden border-2 border-charcoal/10" />
                <p className="mt-3 text-xs text-charcoal/60 text-center">
                  Centrez le QR code Tagora dans le cadre.
                </p>
              </div>
            </div>
          </div>
        )}

        {result && (
          <div className="card-brutal p-6" data-testid="verify-result">
            {!result.valid_format ? (
              <div className="text-center">
                <ShieldAlert className="w-14 h-14 text-danger mx-auto mb-3" strokeWidth={2.5} />
                <h2 className="text-xl font-bold font-heading mb-1">Code invalide</h2>
                <p className="text-charcoal/60 text-sm">{result.message || 'Format ou checksum incorrect.'}</p>
                <p className="text-xs text-charcoal/40 mt-3">Le format attendu est <code className="font-mono">TGR-XXXX-XXXX-XXXX-XXXX</code>.</p>
              </div>
            ) : !result.exists ? (
              <div className="text-center">
                <ShieldAlert className="w-14 h-14 text-yellow-500 mx-auto mb-3" strokeWidth={2.5} />
                <h2 className="text-xl font-bold font-heading mb-1">Code authentique mais non enregistre</h2>
                <p className="text-charcoal/60 text-sm max-w-lg mx-auto">Ce code respecte le format Tagora ID, mais aucun objet n'est enregistre dessus dans notre base. Il s'agit peut-etre d'un sticker non encore active.</p>
                <code className="inline-block mt-4 font-mono font-bold bg-surface-alt px-3 py-1.5 rounded-md border border-charcoal/10">{result.code}</code>
              </div>
            ) : (
              <div>
                <div className="flex flex-col sm:flex-row items-start gap-5 mb-5">
                  <TagoraSeal tagoraId={result.code} verified />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-secondary">Authentique &amp; enregistre</p>
                    <h2 className="text-2xl font-bold font-heading">Code verifie</h2>
                    <p className="text-sm text-charcoal/60 mt-1">
                      Enregistre le {result.registered_at ? new Date(result.registered_at).toLocaleDateString('fr-FR') : '—'}
                    </p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mb-5">
                  <Status label="Format" value="Valide" ok />
                  <Status label="Statut" value={result.is_active ? 'Actif' : 'Inactif'} ok={result.is_active} />
                  <Status label="Mode perdu" value={result.is_lost ? 'Active' : 'Non'} ok={!result.is_lost} muted={!result.is_lost} />
                  <Status label="Assignation" value={result.is_assigned ? 'Assigne' : 'Disponible'} ok muted={!result.is_assigned} />
                  <Status label="Retire" value={result.is_archived ? 'Oui' : 'Non'} ok={!result.is_archived} muted />
                </div>

                {result.organization && (
                  <div className="border-2 border-charcoal rounded-2xl overflow-hidden mb-4 bg-white" data-testid="verify-org">
                    {result.organization.cover_image_url && (
                      <div className="h-20 sm:h-24 overflow-hidden bg-charcoal/5">
                        <img src={result.organization.cover_image_url} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-4 flex items-start gap-3">
                      {result.organization.logo_url ? (
                        <img src={result.organization.logo_url} alt="" className="w-12 h-12 rounded-xl border-2 border-charcoal/15 object-cover flex-shrink-0" />
                      ) : (
                        <div
                          className="w-12 h-12 rounded-xl border-2 border-charcoal/15 flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: result.organization.primary_color || '#1F2937' }}
                        >
                          <Building2 className="w-5 h-5 text-white" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-charcoal/50">Organisation</p>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-base font-bold truncate">{result.organization.name}</p>
                          {result.organization.domain_verified && (
                            <span
                              className="inline-flex items-center gap-1 text-[9px] font-bold tracking-widest uppercase bg-green-100 text-green-800 border border-green-300 px-1.5 py-0.5 rounded-full"
                              title={`Site verifie: ${result.organization.verified_domain}`}
                              data-testid="verify-org-domain-verified"
                            >
                              <BadgeCheck className="w-3 h-3" /> Site verifie
                            </span>
                          )}
                          {result.organization.is_partner && (
                            <span className="text-[9px] font-bold tracking-widest uppercase bg-primary/30 text-charcoal border border-charcoal/30 px-1.5 py-0.5 rounded-full">
                              Partenaire
                            </span>
                          )}
                        </div>
                        {result.organization.tagline && (
                          <p className="text-xs text-charcoal/65 mt-0.5 leading-snug">{result.organization.tagline}</p>
                        )}
                        {(result.organization.website_url || result.organization.verified_domain) && (
                          <a
                            href={result.organization.website_url || `https://${result.organization.verified_domain}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1.5 font-bold"
                            data-testid="verify-org-website-link"
                          >
                            <Globe className="w-3 h-3" />
                            {(result.organization.verified_domain) || (result.organization.website_url || '').replace(/^https?:\/\//, '').replace(/\/$/, '')}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {result.history && result.history.length > 0 && (
                  <div className="border-2 border-charcoal/10 rounded-xl p-4 mb-4" data-testid="verify-history">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-4 h-4 text-charcoal/60" />
                      <h3 className="text-[11px] font-bold uppercase tracking-widest text-charcoal/60">Historique du Tagora</h3>
                    </div>
                    <ol className="relative border-l-2 border-charcoal/15 ml-2 space-y-3">
                      {result.history.map((evt, i) => (
                        <li key={i} className="ml-4 relative" data-testid={`verify-history-event-${evt.type}`}>
                          <span className={`absolute -left-[19px] top-0.5 flex items-center justify-center w-4 h-4 rounded-full border-2 ${evtBadgeStyle(evt.type)}`}>
                            <EvtIcon type={evt.type} />
                          </span>
                          <p className="text-sm font-bold text-charcoal leading-tight">{evt.label}</p>
                          <p className="text-xs text-charcoal/55 font-mono mt-0.5">
                            {evt.date ? new Date(evt.date).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </p>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mt-4">
                  <Link to={`/ping/${result.code}`} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="verify-open-ping">
                    <MapPin className="w-4 h-4" /> Voir la fiche du PingMe
                  </Link>
                  <Link to="/label" className="btn-outline inline-flex items-center gap-2 text-sm">
                    <ExternalLink className="w-4 h-4" /> A propos du standard
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Status = ({ label, value, ok, muted }) => (
  <div className={`flex items-center justify-between p-2.5 rounded-lg border-2 ${ok ? (muted ? 'border-charcoal/15 bg-white' : 'border-secondary/40 bg-secondary/10') : 'border-danger/40 bg-danger/5'}`}>
    <span className="text-[10px] font-bold uppercase tracking-wider text-charcoal/50">{label}</span>
    <span className={`text-sm font-bold ${ok ? 'text-charcoal' : 'text-danger'}`}>{value}</span>
  </div>
);

const EvtIcon = ({ type }) => {
  const cls = "w-2.5 h-2.5";
  if (type === 'created') return <Sparkles className={cls} strokeWidth={3} />;
  if (type === 'claimed') return <UserCheck className={cls} strokeWidth={3} />;
  if (type === 'transferred') return <ArrowRightLeft className={cls} strokeWidth={3} />;
  if (type === 'archived') return <Archive className={cls} strokeWidth={3} />;
  return null;
};

const evtBadgeStyle = (type) => {
  switch (type) {
    case 'created': return 'bg-primary/30 border-charcoal text-charcoal';
    case 'claimed': return 'bg-secondary/40 border-charcoal text-charcoal';
    case 'transferred': return 'bg-accent/40 border-charcoal text-charcoal';
    case 'archived': return 'bg-charcoal/10 border-charcoal/40 text-charcoal/70';
    default: return 'bg-white border-charcoal text-charcoal';
  }
};

export default Verify;
